import { SiteFooter } from "@components/layout/SiteFooter";
import { SiteHeader } from "@components/layout/SiteHeader";
import { HelpCategory } from "@components/help/help-category";
import { TopBanner } from "@components/help/top-banner";
import { useLocalStorage } from "@hooks/useLocalStorage";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/ui";
import { useTranslations } from "@services/strings";
import { allFaqDocuments, FaqDocument } from "contentlayer/generated";
import { GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect } from "react";

/** General first, then the products in the order the home page lists them. */
const CATEGORIES = [
  "general",
  "unlock",
  "payLess",
  "mortgage",
  "auto",
] as const;

const inCategory = (
  faqDocuments: FaqDocument[],
  category: FaqDocument["faq_category"],
) => faqDocuments.filter((faq) => faq.faq_category === category);

const Page = ({ faqDocuments }: { faqDocuments: FaqDocument[] }) => {
  const { query } = useRouter();
  const isApp = query.app === "true";

  const [lsIsApp, setValue] = useLocalStorage("is-app", undefined);

  useEffect(() => {
    if (isApp) {
      setValue(isApp);
    }
  }, [isApp, setValue]);

  const { t } = useTranslations();
  return (
    <>
      <Head>
        <title>{t("pages.help.title")}</title>
        <meta
          name="description"
          content={t("pages.help.banner.description")}
        ></meta>
      </Head>
      {lsIsApp ? null : <SiteHeader />}
      <main className="min-h-screen">
        {lsIsApp ? null : <TopBanner />}
        <div className="container pt-8 md:pt-12">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t("pages.help.title")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        {CATEGORIES.map((category, index) => (
          <HelpCategory
            key={category}
            category={category}
            faqDocuments={inCategory(faqDocuments, category)}
            tone={index % 2 ? "mist" : "white"}
          />
        ))}
      </main>
      {lsIsApp ? null : <SiteFooter />}
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const faqDocuments = allFaqDocuments;
  const currentLocale = context.locale ?? "en-EG";

  // Filter the faq documents to only include the current locale
  const filteredFaqDocuments = faqDocuments.filter(
    (faq) => faq.locale === currentLocale,
  );

  // sort by order property, default to date if order is not defined
  filteredFaqDocuments.sort((a, b) => {
    const orderA = a.order ?? Infinity;
    const orderB = b.order ?? Infinity;

    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  return {
    props: {
      faqDocuments: filteredFaqDocuments,
    },
  };
};

export default Page;
